import {
  useEffect,
  useRef,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { MessageSquarePlus, PanelRightClose } from "lucide-react";

import { AccountMenu } from "@/components/AccountMenu";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AnimatedSidebarTrigger } from "@/components/motion/animated-sidebar";
import { ASSISTANT_PANEL_ID } from "@/components/overlay-bot/assistantPanelChrome";
import { SidebarCollapseIcon, SidebarExpandIcon } from "@/components/ui/sidebar";
import {
  MORPH_REDUCED_CROSSFADE_MS,
  PANEL_EASE,
  PANEL_ICON_SWAP_MS,
  PANEL_PRESS_SCALE,
  toSeconds,
} from "@/guide/morph/morphTimeline";
import { SPRING_PRESS } from "@/lib/ease";
import { cn } from "@/lib/utils";

/**
 * Einklapp-Schalter der Shell-Sidebar (Animated-Sidebar) in der Größe der
 * jeweiligen Kopfzeile — der Standardknopf der Komponente ist mit 2.5rem zu
 * groß für beide.
 *
 * Welches der beiden Symbole gilt, entscheidet `data-state` am Knopf selbst
 * (die Komponente setzt es): So braucht diese Kopfzeile den Sidebar-Kontext
 * nicht zu lesen — sie steht auch außerhalb des Providers, wo ein Hook auf
 * ihn eine Ausnahme wäre.
 */
function ShellSidebarTrigger({ className }: { className?: string }) {
  return (
    <AnimatedSidebarTrigger
      data-testid="app-sidebar-trigger"
      className={cn("group shrink-0 rounded-md transition-colors", className)}
    >
      <SidebarCollapseIcon className="group-data-[state=collapsed]:hidden" />
      <SidebarExpandIcon className="group-data-[state=expanded]:hidden" />
    </AnimatedSidebarTrigger>
  );
}

/**
 * The icon of the „Fragen" button as **state**, not decoration: closed
 * (`MessageSquarePlus`, "you can ask something here") versus open
 * (`PanelRightClose`, "the panel on the right closes again"). The switch runs
 * via `AnimatePresence` — the old icon rotates out, the new one in, both at the
 * same time over the same area.
 *
 * Absolutely positioned in a fixed-size box so the swap doesn't touch the
 * button width: two icons side by side in the flow would shift the label next
 * to them for 200 ms.
 *
 * With `prefers-reduced-motion`, all that remains is a crossfade over
 * `MORPH_REDUCED_CROSSFADE_MS` — the same number as in the morph, no rotation,
 * no scaling.
 */
function AssistantToggleIcon({ open, box }: { open: boolean; box: string }) {
  const reduce = useReducedMotion() ?? false;
  const Icon = open ? PanelRightClose : MessageSquarePlus;
  return (
    <span
      aria-hidden="true"
      data-testid="assistant-toggle-icon"
      data-state={open ? "open" : "closed"}
      className={cn("relative inline-flex shrink-0 items-center justify-center", box)}
    >
      <AnimatePresence initial={false}>
        <motion.span
          key={open ? "open" : "closed"}
          className="absolute inset-0 inline-flex items-center justify-center"
          initial={reduce ? { opacity: 0 } : { opacity: 0, rotate: -90, scale: 0.6 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, rotate: 0, scale: 1 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, rotate: 90, scale: 0.6 }}
          transition={{
            duration: toSeconds(reduce ? MORPH_REDUCED_CROSSFADE_MS : PANEL_ICON_SWAP_MS),
            ease: PANEL_EASE,
          }}
        >
          <Icon className={box} />
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

interface Properties {
  title: string;
  /**
   * `chat`: the chat page's header. Formerly carried an "aktiv" pill while the assistant
   * worked on the open conversation; removed 28.09.2026 (the steps bar in the transcript
   * shows the state). Kept as a variant so callers stay untouched.
   */
  variant?: "default" | "chat";
  /** Zustand/Toggle des rechts andockenden "Fragen"-Panels (AppShell). */
  assistantOpen?: boolean;
  onToggleAssistant?: () => void;
  /**
   * Eigenes Bedienelement ganz links, direkt nach dem Einklapp-Schalter —
   * für Seiten ohne Shell-Sidebar (Chat) die Stelle für Kundenlogo/Home
   * bzw. einen eigenen Listen-Umschalter.
   */
  leading?: ReactNode;
  /**
   * Einklapp-Schalter der Shell-Sidebar zeigen. Außerhalb eines
   * `AnimatedSidebarProvider` auf `false` setzen — der Schalter liest sonst
   * einen Kontext, den es dort nicht gibt.
   */
  showSidebarTrigger?: boolean;
}

/**
 * Kompakte Kopfzeile der Sidebar-Seiten nach dem site-header des
 * shadcn-dashboard-01-Blocks: Einklapp-Schalter, vertikale Trennlinie,
 * linksbündiger Seitentitel; rechts "Fragen" (Assistenten-Panel) und der
 * das Kontomenü als Avatar mit dem Sprachwechsel darin (auf allen
 * Gerätegrößen; die Sidebar trägt keinen Nutzer-Footer mehr). Mobil
 * entfallen Trennlinie und Rahmen-Kästen.
 */
export function SiteHeader({
  title,
  assistantOpen,
  onToggleAssistant,
  leading,
  showSidebarTrigger = true,
}: Properties) {
  const { t } = useTranslation();
  const reduce = useReducedMotion() ?? false;

  /**
   * The most recently pressed „Fragen" button. There are two of them (mobile
   * and desktop), one of which is always hidden via media query — JavaScript
   * doesn't know which one applies, but the click does.
   */
  const lastToggleRef = useRef<HTMLButtonElement | null>(null);
  const wasOpenRef = useRef(assistantOpen);

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    lastToggleRef.current = button;
    // On **close**, pull focus here immediately instead of looking for it
    // afterwards: Safari and Firefox on macOS don't focus a `<button>` on mouse
    // click, so focus would still sit in the panel's `textarea` — and the panel
    // is now sliding out (`AnimatePresence`), so it's still alive. The guard in
    // the effect below would see "there is focus, so hands off", and 240 ms
    // later focus would fall to `body` along with the node.
    if (assistantOpen) button.focus();
    onToggleAssistant?.();
  };

  /**
   * Focus back on the button when the panel closes — the safety net under the
   * synchronous grab in `handleToggle`, for every close that doesn't originate
   * from the button. On open, `OverlayBotChat` pulls focus into the input line
   * (`active`); on close that element disappears, and a keyboard user would end
   * up at the top of the page.
   *
   * "Clicked somewhere else" is the only situation in which nothing may happen.
   * An empty `body` is no longer a reliable sign of that: the panel slides out,
   * so while this effect runs it is still in the DOM and holds focus — which is
   * why anything **inside** the panel shell also counts as "focus is about to be
   * gone".
   */
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = assistantOpen;
    if (!wasOpen || assistantOpen) return;
    const active = document.activeElement;
    const inPanel =
      active instanceof Element && active.closest(`#${ASSISTANT_PANEL_ID}`) !== null;
    if (active && active !== document.body && !inPanel) return;
    lastToggleRef.current?.focus();
  }, [assistantOpen]);

  // Chat und übrige Shell-Seiten teilen sich exakt diese eine Kopfzeile;
  // der Weg zum Dashboard steht im Chat als Menüzeile in der Seitenleiste.
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear">
      <div className="flex w-full min-w-0 items-center gap-1 px-4 lg:gap-2 lg:px-6">
        {showSidebarTrigger ? (
          <ShellSidebarTrigger className="-ml-1 size-9 text-muted-foreground hover:bg-accent hover:text-accent-foreground [&_svg]:size-5" />
        ) : null}
        {leading}
        {showSidebarTrigger || leading ? (
          <Separator orientation="vertical" className="mx-2 h-4 max-sm:hidden" />
        ) : null}
        <h1 className="min-w-0 truncate text-base font-semibold max-sm:ml-1">{title}</h1>
        <div className="ml-auto flex shrink-0 items-center gap-1 max-sm:gap-4">
          {onToggleAssistant ? (
            <>
              {/* On mobile only the icon, without a border — and therefore the
                  only carrier of the name: here the `aria-label` changes with
                  the state. */}
              <Button
                asChild
                variant="ghost"
                size="icon"
                className={cn(
                  "sm:hidden",
                  assistantOpen && "bg-accent text-accent-foreground",
                )}
              >
                <motion.button
                  data-guide-id="header.assistant"
                  type="button"
                  aria-expanded={assistantOpen}
                  aria-controls={ASSISTANT_PANEL_ID}
                  aria-label={
                    assistantOpen ? t("overlayBot.close") : t("overlayBot.ask")
                  }
                  onClick={handleToggle}
                  whileTap={reduce ? undefined : { scale: PANEL_PRESS_SCALE }}
                  transition={SPRING_PRESS}
                >
                  <AssistantToggleIcon open={Boolean(assistantOpen)} box="size-5" />
                </motion.button>
              </Button>
              {/* On desktop the button shows its label visibly. It stays
                  „Fragen" even when the panel is open, and there is **no**
                  `aria-label` on top: the accessible name must contain the
                  visible text (WCAG 2.5.3, "Label in Name"), and
                  „Assistent schließen" wouldn't. The state is carried by
                  `aria-expanded` (with `aria-controls` pointing at the panel
                  shell — the disclosure pattern; `aria-pressed` alongside would
                  announce the state twice), the icon and the background.

                  The **variant** stays `outline` throughout, and only the
                  background changes — just like the mobile button next to it.
                  `secondary` has no border: switching outline↔secondary would
                  cost the button 2 px of width under `box-sizing: border-box`,
                  the label would shift 1 px, and because the row is
                  right-aligned via `ml-auto`, everything to its left would jump
                  along. Via `transition-colors` (base class) only the color
                  animates. */}
              <Button
                asChild
                variant="outline"
                size="sm"
                className={cn(
                  "gap-1.5 max-sm:hidden",
                  assistantOpen && "bg-accent text-accent-foreground",
                )}
              >
                <motion.button
                  data-guide-id="header.assistant"
                  type="button"
                  aria-expanded={assistantOpen}
                  aria-controls={ASSISTANT_PANEL_ID}
                  onClick={handleToggle}
                  whileTap={reduce ? undefined : { scale: PANEL_PRESS_SCALE }}
                  transition={SPRING_PRESS}
                >
                  <AssistantToggleIcon open={Boolean(assistantOpen)} box="size-4" />
                  {t("overlayBot.ask")}
                </motion.button>
              </Button>
            </>
          ) : null}
          {/* Kontomenü für alle Gerätegrößen: Avatar oben rechts, darin auch der Sprachwechsel. */}
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}
